import {
    bcbs_config_attribute,
    bcbs_config_attribute_bcbs_config_secti,
    bcbs_config_category,
    bcbs_config_masterattribute,
    bcbs_config_row,
    bcbs_config_section,
    bcbs_config_tab
} from "./CRM";
import { IGroupConfigurationConfig } from "./FlexibleAttributeModel";
import { $cat_$non_standard_id } from "model/ConfigCategoryConstants";

export interface IFlexibleFormDefinition {
    tabs: IFlexibleFormTabDefinition[];
    attributes: IFlexibleFormAttributeDefinition[];
}

export interface IFlexibleFormCategoryDefinition extends IFlexibleFormDefinition {
    category: bcbs_config_category;
}

export interface IFlexibleFormTabDefinition {
    tab: bcbs_config_tab;
    sections: IFlexibleFormSectionDefinition[];
    attributes: IFlexibleFormAttributeDefinition[];
}

export interface IFlexibleFormSectionDefinition {
    section: bcbs_config_section;
    rows: IFlexibleFormRowDefinition[];
    attributes: IFlexibleFormAttributeDefinition[];
}

export interface IFlexibleFormRowDefinition {
    row: bcbs_config_row;
    attributes: IFlexibleFormAttributeDefinition[];

    uniqueMasterAttributes: Record<string, IFlexibleFormAttributeDefinition[]>; // master attribute id to attribute definition
}

export interface IFlexibleFormAttributeDefinition {
    bcbs_config_attribute: bcbs_config_attribute;
    bcbs_config_masterattribute: bcbs_config_masterattribute;

    bcbs_config_tab: bcbs_config_tab;
    bcbs_config_section: bcbs_config_section;
}

export interface IFlexibleFormAttributeSetDefinition extends IFlexibleFormDefinition {
    attributeSetDefinition: IFlexibleFormAttributeDefinition;

}

export interface IFlexibleFormNonStandardDefinition extends IFlexibleFormDefinition {
    section: bcbs_config_section;
}

// TODO Tempo. adding attributes to section until rows are populated
/**
 * We use this to prune the Form definition of empty tabs, sections, and rows.
 * @param flexibleFormDefinition the form definition
 */
function pruneEmptyFormDefinitionElements(flexibleFormDefinition: IFlexibleFormDefinition): void {
    if (!flexibleFormDefinition)
        return;

    for (let i = flexibleFormDefinition.tabs.length - 1; i >= 0; i--) {
        let tab = flexibleFormDefinition.tabs[i];

        if (tab.tab.bcbs_isalwaysvisible)
            continue;

        // check if the tab is empty
        if (tab.sections.length === 0) {
            flexibleFormDefinition.tabs.splice(i, 1);
            continue;
        }

        for (let j = tab.sections.length - 1; j >= 0; j--) {
            let section = tab.sections[j];

            if (section.section.bcbs_isalwaysvisible)
                continue;

            // check if the section is empty
            if (section.rows.length === 0) {
                tab.sections.splice(j, 1);
                continue;
            }

            for (let k = section.rows.length - 1; k >= 0; k--) {
                let row = section.rows[k];

                // check if the row is empty
                if (row.attributes.length === 0) {
                    section.rows.splice(k, 1);
                    continue;
                }
            }


            // once we process all the rows of the section
            // we need to recheck the row count
            if (section.rows.length === 0) {
                tab.sections.splice(j, 1);
                continue;
            }
        }

        // once we process all the sections of the tab
        // we need to recheck the section count
        if (tab.sections.length === 0) {
            flexibleFormDefinition.tabs.splice(i, 1);
            continue;
        }
    }
}

// /**
//  * We use this to prune the Form definition of empty tabs, sections, and rows.
//  * @param flexibleFormDefinition the form definition
//  */
// function pruneEmptyFormDefinitionElements_no_rows(flexibleFormDefinition: IFlexibleFormDefinition): void {
//     if (!flexibleFormDefinition)
//         return;

//     for (let i = flexibleFormDefinition.tabs.length - 1; i >= 0; i--) {
//         let tab = flexibleFormDefinition.tabs[i];

//         if (tab.tab.bcbs_isalwaysvisible)
//             continue;

//         // check if the tab is empty
//         if (tab.sections.length === 0) {
//             flexibleFormDefinition.tabs.splice(i, 1);
//             continue;
//         }

//         for (let j = tab.sections.length - 1; j >= 0; j--) {
//             let section = tab.sections[j];

//             if (section.section.bcbs_isalwaysvisible)
//                 continue;

//             // check if the section is empty
//             if (section.attributes.length === 0) {
//                 tab.sections.splice(j, 1);
//                 continue;
//             }
//         }

//         // once we process all the sections of the tab
//         // we need to recheck the section count
//         if (tab.sections.length === 0) {
//             flexibleFormDefinition.tabs.splice(i, 1);
//             continue;
//         }
//     }
// }

/**
 * Builds the Form Definition for all categories. The form definition defines all the tabs,
 * all the tab's sections, all the tab's section's rows, and all the tab's section's row's attributes.
 * This structure can be used to generate the UI or to generate the fields for a given category.
 * @param config the release configuration data
 * @returns A mapping of category id to the category form definition
 */
export function buildCategoryFormDefinitions(config: IGroupConfigurationConfig): Record<string, IFlexibleFormCategoryDefinition> {
    let categories: Record<string, IFlexibleFormCategoryDefinition> = {};

    let maMap = new Map<string, bcbs_config_masterattribute>();
    config.masterattributes.forEach(ma => {
        maMap.set(ma.bcbs_config_masterattributeid!, ma);
    });

    config.categories.forEach(cat => {
        let uicat: IFlexibleFormCategoryDefinition = {
            category: cat,
            tabs: [],
            attributes: []
        };

        categories[cat.bcbs_config_categoryid!] = uicat;
    });

    let tabMap = new Map<string, IFlexibleFormTabDefinition>();
    config.tabs.forEach(tab => {
        if (!tab || !tab._bcbs_config_categoryid_value)
            return;

        let cat = categories[tab._bcbs_config_categoryid_value];
        if (!cat)
            return;

        let uitab: IFlexibleFormTabDefinition = {
            tab: tab,
            sections: [],
            attributes: [],
        };

        cat.tabs.push(uitab);
        tabMap.set(tab.bcbs_config_tabid!, uitab);
    });

    let sectionMap = new Map<string, IFlexibleFormSectionDefinition>();
    config.sections.forEach(section => {
        if (!section || !section._bcbs_config_tabid_value)
            return;

        let tab = tabMap.get(section._bcbs_config_tabid_value);
        if (!tab)
            return;

        let uisection: IFlexibleFormSectionDefinition = {
            section: section,
            rows: [],
            attributes: []
        };

        tab.sections.push(uisection);
        sectionMap.set(section.bcbs_config_sectionid!, uisection);
    });

    let rowMap = new Map<string, IFlexibleFormRowDefinition>();
    config.rows.forEach(row => {
        if (!row || !row._bcbs_config_sectionid_value)
            return;

        let section = sectionMap.get(row._bcbs_config_sectionid_value);
        if (!section)
            return;

        let uirow: IFlexibleFormRowDefinition = {
            row: row,
            attributes: [],
            uniqueMasterAttributes: {},
        }

        section.rows.push(uirow);
        rowMap.set(row.bcbs_config_rowid!, uirow);
    });

    const processedAttributes: Record<string, IFlexibleFormAttributeDefinition[]> = {};

    config.attributes.forEach(bcbs_config_attribute => {

        if (!bcbs_config_attribute)
            return;

        // skip attribute set attributes
        if (bcbs_config_attribute._bcbs_masterattributesetid_value)
            return;

        // skip non-standard attributes
        if (bcbs_config_attribute.bcbs_nonstandard)
            return;

        if (!bcbs_config_attribute._bcbs_config_masterattributeid_value)
            return;

        // // make sure it's for the current row
        if (!bcbs_config_attribute._bcbs_config_rowid_value)
            return;

        let row = rowMap.get(bcbs_config_attribute._bcbs_config_rowid_value);
        if (!row)
            return;

        // TODO Tempo. adding attributes to section until rows are populated
        if (!bcbs_config_attribute._bcbs_config_sectionid_value)
            return;

        // TODO Tempo. adding attributes to section until rows are populated
        let sectionDef = sectionMap.get(bcbs_config_attribute._bcbs_config_sectionid_value);
        if (!sectionDef)
            throw new Error("Section not found for attribute: " + bcbs_config_attribute.bcbs_config_attributeid);


        let bcbs_config_masterattribute = maMap.get(bcbs_config_attribute._bcbs_config_masterattributeid_value);

        if (!bcbs_config_masterattribute)
            throw new Error("Master attribute not found for attribute: " + bcbs_config_attribute.bcbs_config_attributeid);


        const bcbs_config_section = config.cache.map_bcbs_config_section[bcbs_config_attribute._bcbs_config_sectionid_value!];
        const bcbs_config_tab = config.cache.map_bcbs_config_tab[bcbs_config_section._bcbs_config_tabid_value!];

        let attributeDef: IFlexibleFormAttributeDefinition = {
            bcbs_config_attribute: bcbs_config_attribute,
            bcbs_config_masterattribute: bcbs_config_masterattribute,
            bcbs_config_tab: bcbs_config_tab,
            bcbs_config_section: bcbs_config_section,
        };

        row.attributes.push(attributeDef);

        const masterSet = row.uniqueMasterAttributes[bcbs_config_masterattribute.bcbs_config_masterattributeid!] ?? [];
        masterSet.push(attributeDef);
        row.uniqueMasterAttributes[bcbs_config_masterattribute.bcbs_config_masterattributeid!] = masterSet;

    });

    for (let key in categories) {
        let category = categories[key];
        pruneEmptyFormDefinitionElements(category);
    }

    for (let key in categories) {
        let category = categories[key];
        category.tabs.forEach(tab => {
            tab.sections.forEach(section => {
                if (section.attributes.length > 1) {
                    throw new Error("Section attributes should be empty");
                }

                section.rows.forEach(row => {
                    row.attributes.forEach(attr => {
                        section.attributes.push(attr);
                    })
                });
            })
        })
    }

    return categories;
}

/**
 * Builds the Form Definition for all attribute sets. The form definition defines all the tabs,
 * all the tab's sections, all the tab's section's rows, and all the tab's section's row's attributes.
 * This structure can be used to generate the UI or to generate the fields for a given attribute set.
 * @param config the release configuration data
 * @returns A mapping of attribute set master attribute id to the attribute set form definition
 */
export function buildAttributeSetFormDefinitions(config: IGroupConfigurationConfig): Record<string, IFlexibleFormAttributeSetDefinition> {
    let maMap = new Map<string, bcbs_config_masterattribute>();
    config.masterattributes.forEach(ma => {
        maMap.set(ma.bcbs_config_masterattributeid!, ma);
    });

    let categoryMap = new Map<string, bcbs_config_category>();
    config.categories.forEach(cat => {
        categoryMap.set(cat.bcbs_config_categoryid!, cat);
    });

    let attributeSetIds: string[] = [];
    let attributeSets: Record<string, IFlexibleFormAttributeSetDefinition> = {};

    // build the attribute set lookup
    config.attributes.forEach(bcbs_config_attribute => {
        if (!bcbs_config_attribute)
            return;

        if (!bcbs_config_attribute._bcbs_config_masterattributeid_value)
            return;

        let bcbs_config_masterattribute = maMap.get(bcbs_config_attribute._bcbs_config_masterattributeid_value);

        if (!bcbs_config_masterattribute)
            return;

        // skip attributes that are NOT attribute sets
        if (bcbs_config_masterattribute.bcbs_attributetypecode !== 808900015)
            return;

        const bcbs_config_section = config.cache.map_bcbs_config_section[bcbs_config_attribute._bcbs_config_sectionid_value!];
        const bcbs_config_tab = config.cache.map_bcbs_config_tab[bcbs_config_section._bcbs_config_tabid_value!];

        attributeSetIds.push(bcbs_config_attribute._bcbs_config_masterattributeid_value);
        attributeSets[bcbs_config_attribute._bcbs_config_masterattributeid_value] = {
            attributeSetDefinition: {
                bcbs_config_attribute: bcbs_config_attribute,
                bcbs_config_masterattribute: bcbs_config_masterattribute,
                bcbs_config_tab: bcbs_config_tab,
                bcbs_config_section: bcbs_config_section,
            },
            tabs: [],
            attributes: [],
        };
    });

    // now, for each attributeset, we need to determine the UI hierarchy
    attributeSetIds.forEach(attrsetid => {

        let tabMap = new Map<string, IFlexibleFormTabDefinition>();
        config.tabs.forEach(tab => {
            if (!tab || !tab._bcbs_config_categoryid_value)
                return;

            let uitab: IFlexibleFormTabDefinition = {
                tab: tab,
                sections: [],
                attributes: [],
            };

            attributeSets[attrsetid].tabs.push(uitab);
            tabMap.set(tab.bcbs_config_tabid!, uitab);
        });

        let sectionMap = new Map<string, IFlexibleFormSectionDefinition>();
        config.sections.forEach(section => {
            if (!section || !section._bcbs_config_tabid_value)
                return;

            let tab = tabMap.get(section._bcbs_config_tabid_value);
            if (!tab)
                return;

            let uisection: IFlexibleFormSectionDefinition = {
                section: section,
                rows: [],
                attributes: []
            };

            tab.sections.push(uisection);
            sectionMap.set(section.bcbs_config_sectionid!, uisection);
        });

        let rowMap = new Map<string, IFlexibleFormRowDefinition>();
        config.rows.forEach(row => {
            if (!row || !row._bcbs_config_sectionid_value)
                return;

            let section = sectionMap.get(row._bcbs_config_sectionid_value);
            if (!section)
                return;

            let uirow: IFlexibleFormRowDefinition = {
                row: row,
                attributes: [],
                uniqueMasterAttributes: {},
            }

            section.rows.push(uirow);
            rowMap.set(row.bcbs_config_rowid!, uirow);
        });

        config.attributes.forEach(attr => {
            if (!attr)
                return;

            // make sure attribute belongs to this attribute set 
            if (attr._bcbs_masterattributesetid_value !== attrsetid)
                return;

            // make sure it has a row reference
            if (!attr._bcbs_config_rowid_value)
                return;

            // skip non-attribute set attributes
            if (!attr._bcbs_masterattributesetid_value)
                return;

            // skip non-standard attributes
            if (attr.bcbs_nonstandard)
                return;

            if (!attr._bcbs_config_masterattributeid_value)
                return;

            let row = rowMap.get(attr._bcbs_config_rowid_value);
            if (!row)
                return;


            // TODO Tempo. adding attributes to section until rows are populated
            if (!attr._bcbs_config_sectionid_value)
                return;

            // TODO Tempo. adding attributes to section until rows are populated
            let section = sectionMap.get(attr._bcbs_config_sectionid_value);
            if (!section)
                return;


            let mattr = maMap.get(attr._bcbs_config_masterattributeid_value);

            if (!mattr)
                return;

            let uiattr: IFlexibleFormAttributeDefinition = {
                bcbs_config_attribute: attr,
                // category: cat,
                bcbs_config_masterattribute: mattr,
                // row: row, 
                // section: section,
                // tab: tab
            };

            row.attributes.push(uiattr);
        });


        // if (attrsetid === "982c677f-6270-ef11-a670-0022482800f2")
        //     debugger;

        pruneEmptyFormDefinitionElements(attributeSets[attrsetid]);
    });


    for (let key in attributeSets) {
        let nonStandard = attributeSets[key];
        nonStandard.tabs.forEach(tab => {
            tab.sections.forEach(section => {
                if (section.attributes.length > 1) {
                    throw new Error("Section attributes should be empty");
                }

                section.rows.forEach(row => {
                    row.attributes.forEach(attr => {
                        section.attributes.push(attr);
                    })
                });
            })
        })
    }

    return attributeSets;
}

/**
 * Builds the Form Definition for all non-standards. The form definition defines all the tabs,
 * all the tab's sections, all the tab's section's rows, and all the tab's section's row's attributes.
 * This structure can be used to generate the UI or to generate the fields for a non-standard on a given section.
 * @param config the release configuration data
 * @returns A mapping of section id to the non-standard form definition
 */
export function buildNonStandardFormDefinitions(config: IGroupConfigurationConfig): Record<string, IFlexibleFormNonStandardDefinition> {
    let maMap = new Map<string, bcbs_config_masterattribute>();
    config.masterattributes.forEach(ma => {
        maMap.set(ma.bcbs_config_masterattributeid!, ma);
    });

    let categoryMap = new Map<string, bcbs_config_category>();
    config.categories.forEach(cat => {
        categoryMap.set(cat.bcbs_config_categoryid!, cat);
    });

    // non-standards are defined at the section level
    let sections = new Map<string, bcbs_config_section>();

    // iterate all sections and grab those that are marked enable non-standards
    config.sections.forEach((section) => {
        if (!section)
            return;

        if (!section.bcbs_isenablenonstandard)
            return;

        sections.set(section.bcbs_config_sectionid!, section);
    });

    // create a map of sectionid+attributeid
    // we'll use this later to make sure that we're adding the right fields
    // to the correct sections
    let fieldSections = new Map<string, bcbs_config_attribute_bcbs_config_secti>();
    config.attributeNonStandardSections.forEach(a => {
        if (!a || !a.bcbs_config_sectionid || !a.bcbs_config_attributeid)
            return;

        fieldSections.set(`${a.bcbs_config_sectionid}-${a.bcbs_config_attributeid}`, a);
    });

    let nonStandards: Record<string, IFlexibleFormNonStandardDefinition> = {};

    sections.forEach(nonStandardSection => {
        let tabs: IFlexibleFormTabDefinition[] = [];

        let tabMap = new Map<string, IFlexibleFormTabDefinition>();
        config.tabs.forEach(tab => {
            if (!tab || !tab._bcbs_config_categoryid_value)
                return;

            let uitab: IFlexibleFormTabDefinition = {
                tab: tab,
                sections: [],
                attributes: [],
            };

            tabs.push(uitab);
            tabMap.set(tab.bcbs_config_tabid!, uitab);
        });

        let sectionMap = new Map<string, IFlexibleFormSectionDefinition>();
        config.sections.forEach(section => {
            if (!section || !section._bcbs_config_tabid_value)
                return;

            let tab = tabMap.get(section._bcbs_config_tabid_value);
            if (!tab)
                return;

            let uisection: IFlexibleFormSectionDefinition = {
                section: section,
                rows: [],
                attributes: []
            };

            tab.sections.push(uisection);
            sectionMap.set(section.bcbs_config_sectionid!, uisection);
        });

        let rowMap = new Map<string, IFlexibleFormRowDefinition>();
        config.rows.forEach(row => {
            if (!row || !row._bcbs_config_sectionid_value)
                return;

            let section = sectionMap.get(row._bcbs_config_sectionid_value);
            if (!section)
                return;

            let uirow: IFlexibleFormRowDefinition = {
                row: row,
                attributes: [],
                uniqueMasterAttributes: {},
            }

            section.rows.push(uirow);
            rowMap.set(row.bcbs_config_rowid!, uirow);
        });

        config.attributes.forEach(bcbs_config_attribute => {
            if (!bcbs_config_attribute)
                return;

            if (!bcbs_config_attribute._bcbs_config_rowid_value)
                return;

            // skip attribute set attributes
            if (bcbs_config_attribute._bcbs_masterattributesetid_value)
                return;

            if (!bcbs_config_attribute._bcbs_config_masterattributeid_value)
                return;

            // check if the field is for the section
            let combo = fieldSections.get(`${nonStandardSection.bcbs_config_sectionid}-${bcbs_config_attribute.bcbs_config_attributeid}`);
            if (!combo)
                return;

            let row = rowMap.get(bcbs_config_attribute._bcbs_config_rowid_value);
            if (!row)
                return;

            // TODO Tempo. adding attributes to section until rows are populated
            if (!bcbs_config_attribute._bcbs_config_sectionid_value)
                return;

            // TODO Tempo. adding attributes to section until rows are populated
            let section = sectionMap.get(bcbs_config_attribute._bcbs_config_sectionid_value);
            if (!section)
                return;

            let bcbs_config_masterattribute = maMap.get(bcbs_config_attribute._bcbs_config_masterattributeid_value);

            if (!bcbs_config_masterattribute)
                return;

            let tab = tabMap.get(section.section._bcbs_config_tabid_value ?? '');

            if (!tab)
                return;

            const bcbs_config_section = config.cache.map_bcbs_config_section[bcbs_config_attribute._bcbs_config_sectionid_value!];
            const bcbs_config_tab = config.cache.map_bcbs_config_tab[bcbs_config_section._bcbs_config_tabid_value!];

            let attributeDefinition: IFlexibleFormAttributeDefinition = {
                bcbs_config_attribute: bcbs_config_attribute,
                bcbs_config_masterattribute: bcbs_config_masterattribute,
                bcbs_config_tab: bcbs_config_tab,
                bcbs_config_section: bcbs_config_section,
            };

            row.attributes.push(attributeDefinition);
        });

        let definition: IFlexibleFormNonStandardDefinition = {
            section: nonStandardSection,
            tabs: tabs,
            attributes: [],
        };

        pruneEmptyFormDefinitionElements(definition);

        nonStandards[nonStandardSection.bcbs_config_sectionid!] = definition;
    });


    for (let key in nonStandards) {
        let nonStandard = nonStandards[key];
        nonStandard.tabs.forEach(tab => {
            tab.sections.forEach(section => {
                if (section.attributes.length > 1) {
                    throw new Error("Section attributes should be empty");
                }

                section.rows.forEach(row => {
                    row.attributes.forEach(attr => {
                        section.attributes.push(attr);
                    })
                });
            })
        })
    }

    return nonStandards;
}

export function buildFormDefinitions(config: IGroupConfigurationConfig) {

    // bcbs_config_attributeid to IFlexibleFormAttributeDefinition[]
    const processedAttributes: Record<string, IFlexibleFormAttributeDefinition> = {};

    const categoryFormDefinitions: Record<string, IFlexibleFormCategoryDefinition> = {};
    const attributeSetFormDefinitions: Record<string, IFlexibleFormAttributeSetDefinition> = {};

    // bcbs_config_sectionid to IFlexibleFormNonStandardDefinition
    const nonStandardFormDefinitions: Record<string, IFlexibleFormNonStandardDefinition> = {};

    config.attributes.forEach(bcbs_config_attribute => {

        // Schema Checks:
        // --------------------------------
        if (!bcbs_config_attribute)
            return;

        if (!bcbs_config_attribute._bcbs_config_masterattributeid_value)
            return;

        const bcbs_config_masterattribute = config.cache.map_bcbs_config_masterattribute[bcbs_config_attribute._bcbs_config_masterattributeid_value];

        if (!bcbs_config_masterattribute)
            return;

        const bcbs_config_section = config.cache.map_bcbs_config_section[bcbs_config_attribute._bcbs_config_sectionid_value!];
        const bcbs_config_tab = config.cache.map_bcbs_config_tab[bcbs_config_section._bcbs_config_tabid_value!];

        const attributeDefinition: IFlexibleFormAttributeDefinition = {
            bcbs_config_attribute,
            bcbs_config_masterattribute,
            bcbs_config_tab,
            bcbs_config_section,
        }

        processedAttributes[bcbs_config_attribute.bcbs_config_attributeid!] = attributeDefinition;

        // categoryFormDefinitions --------------------------------
        // TODO exclude attributeSets
        // TODO exclude nonStandards
        // const category = categoryFormDefinitions[attribute._bcbs_config_categoryid_value!];
        // category.attributes.push(attributeDefinition);
        {
            // const bcbs_config_categoryid = attributeDefinition.attribute._bcbs_config_categoryid_value;
            const bcbs_config_rowid = attributeDefinition.bcbs_config_attribute._bcbs_config_rowid_value;
            const bcbs_config_row = config.cache.map_bcbs_config_row[bcbs_config_rowid!];

            const bcbs_config_sectionid = attributeDefinition.bcbs_config_attribute._bcbs_config_sectionid_value;
            const bcbs_config_section = config.cache.map_bcbs_config_section[bcbs_config_sectionid!];

            const bcbs_config_tabid = bcbs_config_section._bcbs_config_tabid_value;
            const bcbs_config_tab = config.cache.map_bcbs_config_tab[bcbs_config_tabid!];

            const bcbs_config_category = config.cache.map_bcbs_config_category[bcbs_config_tab._bcbs_config_categoryid_value!];

            let inValid = false;

            inValid = inValid || !bcbs_config_row;
            inValid = inValid || !bcbs_config_section;
            inValid = inValid || !bcbs_config_masterattribute;
            inValid = inValid || !bcbs_config_attribute._bcbs_config_categoryid_value;
            inValid = inValid || !!bcbs_config_attribute.bcbs_nonstandard;
            inValid = inValid || !!bcbs_config_attribute._bcbs_masterattributesetid_value;

            // if (bcbs_config_section._bcbs_config_categoryid_value !== bcbs_config_category.bcbs_config_categoryid)
            //     throw new Error("Section and Category mismatch detected.");
            // if (bcbs_config_row._bcbs_config_categoryid_value !== bcbs_config_category.bcbs_config_categoryid)
            //     throw new Error("Row and Category mismatch detected.");
            // if (bcbs_config_row._bcbs_config_sectionid_value !== bcbs_config_section.bcbs_config_sectionid)
            //     throw new Error("Row and Section mismatch detected.");
            // check for inconsistencies, row missmatch between attribute and section and tab and category

            // TODO we should consider throwing errors for these inconsistencies instead of silently skipping them
            if (!inValid) {

                let category = categoryFormDefinitions[bcbs_config_category.bcbs_config_categoryid!];

                if (!category) {
                    category = {
                        category: bcbs_config_category,
                        tabs: [],
                        attributes: [],
                    }

                    categoryFormDefinitions[bcbs_config_category.bcbs_config_categoryid!] = category;
                }

                category.attributes.push(attributeDefinition);
            }

        }

        // attributeSetFormDefinitions ---------------------------
        {
            const invalid = !bcbs_config_attribute._bcbs_config_rowid_value;

            if (!invalid) {
                if (bcbs_config_masterattribute.bcbs_attributetypecode === 808900015) {
                    let attributeSet = attributeSetFormDefinitions[bcbs_config_masterattribute.bcbs_config_masterattributeid!]

                    if (!attributeSet) {

                        const ats: IFlexibleFormAttributeDefinition = {
                            bcbs_config_attribute: bcbs_config_attribute,
                            bcbs_config_masterattribute: bcbs_config_masterattribute,
                            bcbs_config_section: bcbs_config_section,
                            bcbs_config_tab: bcbs_config_tab
                        };

                        attributeSet = {
                            attributeSetDefinition: ats,
                            attributes: [],
                            tabs: []
                        };

                        attributeSetFormDefinitions[bcbs_config_masterattribute.bcbs_config_masterattributeid!] = attributeSet;

                    }

                    const ats2: IFlexibleFormAttributeDefinition = {
                        bcbs_config_attribute: bcbs_config_attribute,
                        bcbs_config_masterattribute: bcbs_config_masterattribute,
                        bcbs_config_section: bcbs_config_section,
                        bcbs_config_tab: bcbs_config_tab
                    };

                    attributeSet.attributeSetDefinition = ats2;

                } else if (bcbs_config_attribute._bcbs_masterattributesetid_value) {

                    let attributeSet = attributeSetFormDefinitions[bcbs_config_attribute._bcbs_masterattributesetid_value!]

                    if (!attributeSet) {

                        // Note: attributeSetDefinition is not set here, it will be set when we process the master attribute
                        attributeSet = {
                            attributeSetDefinition: null as any,
                            attributes: [],
                            tabs: [],
                        };
                        attributeSetFormDefinitions[bcbs_config_attribute._bcbs_masterattributesetid_value!] = attributeSet;
                    }

                    attributeSet.attributes.push(attributeDefinition);

                } else {
                    // nothing to do
                }
            }
        }

        // nonStandardFormDefinitions ---------------------------
        // Part 1 - we want to make sure that non-standards are associated with the correct category, tab, and section
        {
            if (bcbs_config_attribute.bcbs_nonstandard) {
                // Non-Standard
                // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&newWindow=true&pagetype=entityrecord&etn=bcbs_config_category&id=a0c3f887-d28d-ec11-b400-0022480b6b1d
                if (attributeDefinition.bcbs_config_attribute._bcbs_config_categoryid_value != $cat_$non_standard_id)
                    throw new Error("Non-standard attributes should be associated with the non-standard category.");

                // General Tab
                // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&newWindow=true&pagetype=entityrecord&etn=bcbs_config_tab&id=d6f34248-d38d-ec11-b400-0022480a9ce3
                const section = config.cache.map_bcbs_config_section[attributeDefinition.bcbs_config_attribute._bcbs_config_sectionid_value!];
                if (section._bcbs_config_tabid_value != 'd6f34248-d38d-ec11-b400-0022480a9ce3')
                    throw new Error("Non-standard attributes should be associated with the General tab.");
            }
        }
    });

    // nonStandardFormDefinitions ---------------------------
    // Part 2 - we want to make sure that all non-standard attributes are included in the form definition for their associated section
    config.attributeNonStandardSections.forEach(relation => {

        const relation_bcbs_config_attributeid = relation.bcbs_config_attributeid;
        const relation_bcbs_config_sectionid = relation.bcbs_config_sectionid;

        const relation_bcbs_config_section = config.cache.map_bcbs_config_section[relation_bcbs_config_sectionid!];

        // if (!relation_bcbs_config_section.bcbs_isenablenonstandard) {
        //     throw new Error(`Non-standard attributes (${relation_bcbs_config_attributeid}) should be associated with sections (${relation_bcbs_config_sectionid}) that are enabled for non-standards.`);
        // }

        const nonstandard_bcbs_config_attribute = config.cache.map_bcbs_config_attribute[relation_bcbs_config_attributeid!];
        const nonstandard_bcbs_config_masterattribute = config.cache.map_bcbs_config_masterattribute[nonstandard_bcbs_config_attribute._bcbs_config_masterattributeid_value!]

        const nonStandardFormDefinition: IFlexibleFormNonStandardDefinition = nonStandardFormDefinitions[relation_bcbs_config_sectionid!] ?? {
            section: relation_bcbs_config_section,
            tabs: [],
            attributes: [],
        }

        const nonstandard_bcbs_config_section = config.cache.map_bcbs_config_section[nonstandard_bcbs_config_attribute._bcbs_config_sectionid_value!];
        const nonstandard_bcbs_config_tab = config.cache.map_bcbs_config_tab[nonstandard_bcbs_config_section._bcbs_config_tabid_value!];

        const attributeDefinition: IFlexibleFormAttributeDefinition = {
            bcbs_config_attribute: nonstandard_bcbs_config_attribute,
            bcbs_config_masterattribute: nonstandard_bcbs_config_masterattribute,
            bcbs_config_tab: nonstandard_bcbs_config_tab,
            bcbs_config_section: nonstandard_bcbs_config_section,
        }

        nonStandardFormDefinition.attributes.push(attributeDefinition);

        nonStandardFormDefinitions[relation_bcbs_config_sectionid!] = nonStandardFormDefinition;
    });

    // Process Tree
    completeFormDefinition(categoryFormDefinitions, config);
    completeFormDefinition(attributeSetFormDefinitions, config);
    completeFormDefinition(nonStandardFormDefinitions, config);

    return {
        categoryFormDefinitions,
        attributeSetFormDefinitions,
        nonStandardFormDefinitions,
    };
}

function completeFormDefinition(formDefinition: Record<string, IFlexibleFormDefinition>, config: IGroupConfigurationConfig) {

    Object.entries(formDefinition).forEach(([key, eachFormDefinition]) => {

        eachFormDefinition.attributes.sort((a, b) => (a.bcbs_config_attribute.bcbs_sortorder ?? 0) - (b.bcbs_config_attribute.bcbs_sortorder ?? 0));

        // adding tabs to form definition
        const tabLookup: Record<string, IFlexibleFormTabDefinition> = {};
        eachFormDefinition.attributes.forEach(eachAttribute => {


            const bcbs_config_sectionid = eachAttribute.bcbs_config_attribute._bcbs_config_sectionid_value;
            const bcbs_config_section = config.cache.map_bcbs_config_section[bcbs_config_sectionid!];

            // tabId has not been stored on attribute, we need to discover it from section
            const bcbs_config_tabid = bcbs_config_section._bcbs_config_tabid_value;
            const bcbs_config_tab = config.cache.map_bcbs_config_tab[bcbs_config_tabid!];

            // check for inconsistencies, row missmatch between attribute and section and tab and category
            let tab = tabLookup[bcbs_config_tabid!];

            if (!tab) {
                tab = {
                    tab: bcbs_config_tab,
                    sections: [],
                    attributes: [],
                };
                tabLookup[bcbs_config_tabid!] = tab;
                eachFormDefinition.tabs.push(tab);
            }

            tab.attributes.push(eachAttribute);
        });

        eachFormDefinition.tabs.sort((a, b) => (a.tab.bcbs_sortorder ?? 0) - (b.tab.bcbs_sortorder ?? 0));

        // adding sections to form definition
        eachFormDefinition.tabs.forEach(tab => {
            const sectionLookup: Record<string, IFlexibleFormSectionDefinition> = {};

            tab.attributes.forEach(eachAttribute => {

                const bcbs_config_sectionid = eachAttribute.bcbs_config_attribute._bcbs_config_sectionid_value;
                const bcbs_config_section = config.cache.map_bcbs_config_section[bcbs_config_sectionid!];

                let section = sectionLookup[bcbs_config_sectionid!];

                if (!section) {
                    section = {
                        section: bcbs_config_section,
                        rows: [],
                        attributes: [],
                    };

                    sectionLookup[bcbs_config_sectionid!] = section;
                    tab.sections.push(section);
                }

                section.attributes.push(eachAttribute);
            });

            tab.sections.sort((a, b) => (a.section.bcbs_sortorder ?? 0) - (b.section.bcbs_sortorder ?? 0));

            // adding rows to form definition
            tab.sections.forEach(eachSection => {
                const rowLookup: Record<string, IFlexibleFormRowDefinition> = {};

                eachSection.attributes.forEach(eachAttribute => {

                    const bcbs_config_rowid = eachAttribute.bcbs_config_attribute._bcbs_config_rowid_value;
                    const bcbs_config_row = config.cache.map_bcbs_config_row[bcbs_config_rowid!];

                    let row = rowLookup[bcbs_config_rowid!];

                    if (!row) {
                        row = {
                            row: bcbs_config_row,
                            attributes: [],
                            uniqueMasterAttributes: {},
                        };

                        rowLookup[bcbs_config_rowid!] = row;
                        eachSection.rows.push(row);
                    }

                    row.attributes.push(eachAttribute);

                    const masterSet = row.uniqueMasterAttributes[eachAttribute.bcbs_config_masterattribute.bcbs_config_masterattributeid!] ?? [];
                    masterSet.push(eachAttribute);
                    row.uniqueMasterAttributes[eachAttribute.bcbs_config_masterattribute.bcbs_config_masterattributeid!] = masterSet;

                });

                eachSection.rows.sort((a, b) => (a.row.bcbs_sortorder ?? 0) - (b.row.bcbs_sortorder ?? 0));

            });



        });
    });
}
